import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  'en': {
    // App title and basic UI
    appTitle: 'Metronome Pro',
    appDescription: 'Professional metronome with advanced rhythm patterns',
    
    // Navigation
    features: 'Features',
    howToUse: 'How to Use',
    
    // Tempo control
    bpm: 'BPM',
    tempo: 'Tempo',
    play: 'Play',
    stop: 'Stop',
    pause: 'Pause',
    tap: 'TAP',
    tapInstruction: 'Tap to set tempo',
    playInstruction: 'Click to play/pause',
    
    // Tempo presets
    tempoPresets: {
      basic: 'Basic',
      classical: 'Classical',
      slow: 'Slow',
      andante: 'Andante',
      moderate: 'Moderate',
      allegro: 'Allegro',
      larghissimo: 'Larghissimo',
      grave: 'Grave',
      lento: 'Lento',
      largo: 'Largo',
      larghetto: 'Larghetto',
      adagio: 'Adagio',
      allegretto: 'Allegretto',
      vivace: 'Vivace',
      presto: 'Presto',
      prestissimo: 'Prestissimo',
      showMore: 'Show Classical Terms',
      showLess: 'Show Less'
    },
    
    // Volume controls
    volumeControl: 'Volume Control',
    mainVolume: 'Main Volume',
    rhythmVolumeAdjust: 'Rhythm Volume',
    mute: 'Mute',
    medium: 'Medium',
    max: 'Max',
    noSubdivisions: 'No subdivisions available for current rhythm.',
    
    // Beat controls
    timeSignature: 'Time Signature',
    noteType: 'Note Type',
    soundType: 'Sound Type',
    soundSelection: 'Sound Selection',
    clickToAccent: 'Tap beats to add/remove accents',
    
    // Sound types
    sounds: {
      woodblock: 'Wood Block',
      click: 'Click',
      drum: 'Drum',
      bell: 'Bell'
    },
    
    // Beat legend
    beatLegend: {
      accent: 'Accent',
      regular: 'Regular',
      current: 'Current'
    },
    
    // Beat tooltips
    beatTooltip: {
      addAccent: 'Click to add accent',
      removeAccent: 'Click to remove accent',
      addSubdivisionAccent: 'Click to accent subdivision',
      removeSubdivisionAccent: 'Click to remove subdivision accent'
    },
    
    // Note types
    noteTypes: {
      quarter: 'Quarter Notes',
      eighth: 'Eighth Notes',
      triplet: 'Eighth Triplets',
      sixteenth: 'Sixteenth Notes',
      quintuplet: 'Quintuplets',
      septuplet: 'Septuplets'
    },
    
    // Sound types (legacy support)
    soundTypes: {
      click: 'Click',
      beep: 'Beep',
      wood: 'Wood Block',
      bell: 'Bell'
    },
    
    // Features page
    featuresTitle: 'Features',
    featuresSubtitle: 'Everything you need for perfect timing',
    feature1: {
      title: 'Adjustable Tempo',
      description: 'Set your tempo from 30 to 240 BPM with precision control'
    },
    feature2: {
      title: 'Multiple Time Signatures',
      description: 'Support for various time signatures from 2/4 to 12/8'
    },
    feature3: {
      title: 'Rhythm Patterns',
      description: 'Choose from quarter, eighth, triplet, and sixteenth note patterns'
    },
    feature4: {
      title: 'Custom Accents',
      description: 'Add accents to any beat or subdivision for complex rhythms'
    },
    feature5: {
      title: 'Volume Control',
      description: 'Independent volume control for each rhythm component'
    },
    feature6: {
      title: 'Sound Options',
      description: 'Multiple sound types including click, beep, wood, and bell'
    },
    
    // How to use page
    howToUseTitle: 'How to Use',
    howToUseSubtitle: 'Get started with your metronome in minutes',
    step1: {
      title: 'Set Your Tempo',
      description: 'Use the tempo slider or tap the TAP button to set your desired BPM'
    },
    step2: {
      title: 'Choose Time Signature',
      description: 'Select your time signature from the dropdown menu'
    },
    step3: {
      title: 'Select Note Type',
      description: 'Choose between quarter notes, eighth notes, triplets, or sixteenth notes'
    },
    step4: {
      title: 'Add Accents',
      description: 'Click on beats to add accents and create complex rhythm patterns'
    },
    step5: {
      title: 'Adjust Volume',
      description: 'Fine-tune the volume for each rhythm component independently'
    },
    step6: {
      title: 'Start Playing',
      description: 'Click the play button to start your metronome'
    },
    
    // Support modal
    supportModal: {
      title: 'Support Our Work',
      subtitle: 'Help us keep this metronome free and continuously improved',
      oneTime: 'One-time Sponsorship',
      monthly: 'Monthly Sponsorship',
      mostPopular: 'Most Popular',
      bestValue: 'Best Value',
      oneTimePayment: 'One-time payment',
      recurringMonthly: 'Recurring monthly',
      choosePlan: 'Choose Plan',
      subscribeNow: 'Subscribe Now',
      securePayment: 'Secure payment powered by Stripe',
      planNames: {
        basic: 'Coffee',
        pro: 'Supporter',
        premium: 'Champion'
      },
      planFeatures: {
        thanksMessage: {
          basic: 'Thank you for your support! ☕',
          pro: 'Thank you for being awesome! 🎵',
          premium: 'Thank you for being amazing! 🏆'
        }
      }
    },
    
    // Navigation additions
    previous: 'Prev',
    next: 'Next',
    
    // Error messages
    errorTitle: 'Page Not Found',
    errorMessage: 'The page you are looking for does not exist.',
    goHome: 'Go Home'
  },
  'zh-TW': {
    // App title and basic UI
    appTitle: '專業節拍器',
    appDescription: '具有進階節奏模式的專業節拍器',
    
    // Navigation
    features: '功能特色',
    howToUse: '使用指南',
    
    // Tempo control
    bpm: 'BPM',
    tempo: '速度',
    play: '播放',
    stop: '停止',
    pause: '暫停',
    tap: '輕觸',
    tapInstruction: '輕觸設定速度',
    playInstruction: '點擊播放/暫停',
    
    // Tempo presets
    tempoPresets: {
      basic: '基本',
      classical: '古典',
      slow: '慢速',
      andante: '行板',
      moderate: '中速',
      allegro: '快速',
      larghissimo: '極慢板',
      grave: '莊板',
      lento: '慢板',
      largo: '廣板',
      larghetto: '小廣板',
      adagio: '柔板',
      allegretto: '小快板',
      vivace: '活潑',
      presto: '急板',
      prestissimo: '最急板',
      showMore: '顯示古典術語',
      showLess: '顯示較少'
    },
    
    // Volume controls
    volumeControl: '音量控制',
    mainVolume: '主音量',
    rhythmVolumeAdjust: '節奏音量',
    mute: '靜音',
    medium: '中等',
    max: '最大',
    noSubdivisions: '當前節奏沒有可用的細分。',
    
    // Beat controls
    timeSignature: '拍號',
    noteType: '音符類型',
    soundType: '聲音類型',
    soundSelection: '聲音選擇',
    clickToAccent: '輕觸節拍以添加/移除重音',
    
    // Sound types
    sounds: {
      woodblock: '木魚',
      click: '點擊聲',
      drum: '鼓聲',
      bell: '鈴聲'
    },
    
    // Beat legend
    beatLegend: {
      accent: '重音',
      regular: '一般',
      current: '當前'
    },
    
    // Beat tooltips
    beatTooltip: {
      addAccent: '點擊添加重音',
      removeAccent: '點擊移除重音',
      addSubdivisionAccent: '點擊添加細分重音',
      removeSubdivisionAccent: '點擊移除細分重音'
    },
    
    // Note types
    noteTypes: {
      quarter: '四分音符',
      eighth: '八分音符',
      triplet: '八分音符三連音',
      sixteenth: '十六分音符',
      quintuplet: '五連音',
      septuplet: '七連音'
    },
    
    // Sound types (legacy support)
    soundTypes: {
      click: '點擊聲',
      beep: '嗶聲',
      wood: '木魚',
      bell: '鈴聲'
    },
    
    // Features page
    featuresTitle: '功能特色',
    featuresSubtitle: '完美節拍所需的一切功能',
    feature1: {
      title: '可調節拍速度',
      description: '可調整範圍從 30 到 240 BPM，精確控制'
    },
    feature2: {
      title: '多種拍號',
      description: '支援從 2/4 到 12/8 的各種拍號'
    },
    feature3: {
      title: '節奏模式',
      description: '可選擇四分、八分、三連音和十六分音符模式'
    },
    feature4: {
      title: '自訂重音',
      description: '為任何節拍或細分添加重音，創造複雜節奏'
    },
    feature5: {
      title: '音量控制',
      description: '每個節奏組件獨立音量控制'
    },
    feature6: {
      title: '聲音選項',
      description: '多種聲音類型，包括點擊、嗶聲、木魚和鈴聲'
    },
    
    // How to use page
    howToUseTitle: '使用指南',
    howToUseSubtitle: '幾分鐘內開始使用您的節拍器',
    step1: {
      title: '設定速度',
      description: '使用速度滑桿或輕觸 TAP 按鈕設定所需的 BPM'
    },
    step2: {
      title: '選擇拍號',
      description: '從下拉選單中選擇您的拍號'
    },
    step3: {
      title: '選擇音符類型',
      description: '在四分音符、八分音符、三連音或十六分音符中選擇'
    },
    step4: {
      title: '添加重音',
      description: '點擊節拍以添加重音並創造複雜的節奏模式'
    },
    step5: {
      title: '調整音量',
      description: '獨立微調每個節奏組件的音量'
    },
    step6: {
      title: '開始播放',
      description: '點擊播放按鈕啟動您的節拍器'
    },
    
    // Support modal
    supportModal: {
      title: '支持我們的工作',
      subtitle: '幫助我們保持這個節拍器免費並持續改進',
      oneTime: '一次性贊助',
      monthly: '月度贊助',
      mostPopular: '最受歡迎',
      bestValue: '最佳價值',
      oneTimePayment: '一次性付款',
      recurringMonthly: '每月定期付款',
      choosePlan: '選擇方案',
      subscribeNow: '立即訂閱',
      securePayment: '由 Stripe 提供安全付款',
      planNames: {
        basic: '咖啡',
        pro: '支持者',
        premium: '冠軍'
      },
      planFeatures: {
        thanksMessage: {
          basic: '謝謝您的支持！ ☕',
          pro: '謝謝您的棒棒支持！ 🎵',
          premium: '謝謝您的超棒支持！ 🏆'
        }
      }
    },
    
    // Navigation additions
    previous: '上一頁',
    next: '下一頁',
    
    // Error messages
    errorTitle: '找不到頁面',
    errorMessage: '您尋找的頁面不存在。',
    goHome: '回到首頁'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first, then browser language, default to English
    const saved = localStorage.getItem('metronome-language');
    if (saved && translations[saved]) {
      return saved;
    }
    
    // Check browser language
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang.startsWith('zh')) {
      return 'zh-TW';
    }
    
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('metronome-language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    // Fallback to English if translation not found
    if (value === undefined) {
      value = translations['en'];
      for (const k of keys) {
        value = value?.[k];
      }
    }
    
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'zh-TW' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};